import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";
import { AddressDbService, EntityinfoAddressPayload } from "../services/address-db.service";

import { Injectable } from "@angular/core";
import { Address } from "@app/address/model/address";
import { DataActions } from "@app/project/store/actions";
import { EffectsUtils } from "@app/root-store/effects/effect-creators";
import { ActionWord } from "@app/shared/resources/action-word";
import { AddressActions } from "../actions";

@Injectable()
export class AddressEffects {
    effectsUtils = new EffectsUtils(this.actions$);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AddressActions.loadSingleSet.request),
            mergeMap(({ addressId }) =>
                DataActions.toAction2()(
                    this.addressDbService.getAddress(addressId).pipe(map(address => ({ address }))),
                    AddressActions.loadSingleSet.success,
                    AddressActions.loadSingleSet.failure
                )
            ),
            catchError(DataActions.toError2(AddressActions.loadSingleSet.failure))
        )
    );

    create$ = this.effectsUtils.createExhaustEffect({
        dbMethod: ({ address, addressType, entityId }) =>
            this.addressDbService.createAddress(address, addressType, entityId).pipe(
                // Fetch the full address with all of the entityinfo
                switchMap(address => this.addressDbService.getAddress(address.id)),
                map(fullAddress => ({ address: fullAddress }))
            ),
        actionSet: AddressActions.createSet,
    });

    update$ = this.effectsUtils.createExhaustEffect({
        dbMethod: ({ address }) => {
            this.dispatchUpdateAndCreateAddressEntityinfos(address);
            return this.addressDbService.updateAddress(address).pipe(
                // Fetch the full address with all of the entityinfo
                switchMap(() => this.addressDbService.getAddress(address.id)),
                map(fullAddress => ({ address: fullAddress }))
            );
        },
        actionSet: AddressActions.updateSet,
    });
    updateSuccess$ = this.effectsUtils.createSuccessEffect(AddressActions.updateSuccess, ActionWord.saved);
    updateFailure$ = this.effectsUtils.createFailureEffect(AddressActions.updateFailure, ActionWord.saved);

    delete$ = this.effectsUtils.createExhaustEffect({
        dbMethod: ({ address }) => this.addressDbService.deleteAddress(address).pipe(map(() => ({ address }))),
        actionSet: AddressActions.deleteSet,
    });
    deleteFailure$ = this.effectsUtils.createFailureEffect(AddressActions.deleteFailure, ActionWord.deleted);

    /**
     * Update address-adjacent properties that don't directly exist on the address table
     * ie update the join table (entityinfo_address) that associates a project's address with company-defined properties (entityinfo like Building Type, Builder Name)
     *
     * @param address
     */
    private dispatchUpdateAndCreateAddressEntityinfos(
        address: Address & { prevType?: number; prevBuilder?: number; prevCommunity?: number }
    ): void {
        const infoToDelete: EntityinfoAddressPayload[] = [];
        const infoToCreate: EntityinfoAddressPayload[] = [];

        // Find changes
        const pushIfChanged = (prevValue: number | null | undefined, newValue: number | null | undefined) => {
            if (prevValue !== newValue) {
                /* eslint-disable @typescript-eslint/naming-convention */
                if (prevValue) {
                    infoToDelete.push({ address_id: address.id, entityinfo_id: +prevValue });
                }
                if (newValue) {
                    infoToCreate.push({ address_id: address.id, entityinfo_id: +newValue });
                }
                /* eslint-enable @typescript-eslint/naming-convention */
            }
        };
        // These prev* props are attached by the form component
        pushIfChanged(address.prevType, address.typeid);
        pushIfChanged(address.prevBuilder, address.builderid);
        pushIfChanged(address.prevCommunity, address.communityid);

        infoToDelete.forEach(info => {
            this.addressDbService.deleteEntityinfoAddress(info);
        });

        // Send updates
        infoToCreate.forEach(info => {
            this.addressDbService.createEntityinfoAddress(info);
        });
    }

    constructor(private readonly actions$: Actions, private readonly addressDbService: AddressDbService) {}
}
